import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { basicDetailsSchema, basicDetailsType } from '../utils';
import { Button, FileUploader, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import SelectDropdown from 'components/base/select-dropdown';
import { FILE_UPLOADER_TOKEN, MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { StoreType } from 'models/store.model';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const BasicDetails = ({ id }: { id: string }) => {
  const { activeCokitchen, cokitchens, refreshStore, setRefreshStore } = useDashboardContext();
  const [details, setDetails] = useState<StoreType>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    getStoreDetails();
  }, [activeCokitchen]);

  const getStoreDetails = async () => {
    if (!activeCokitchen.id) return;
    setLoading(true);
    try {
      const res = await apiService.get(`stores/cokitchen/${activeCokitchen.id}/stores/${id}`);
      setDetails(res.data);
      form.reset({
        name: res.data.name || '',
        type: res.data.type || '',
        location: res.data.location || '',
        logo: res.data.logo || ''
      });
    } catch (e: any) {
      toast(<ToastMessage type='error' message={e.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };
  const form = useForm<basicDetailsType>({
    resolver: zodResolver(basicDetailsSchema),
    defaultValues: {
      name: details?.name || '',
      type: details?.type || '',
      location: details?.location || '',
      logo: details?.logo || ''
    }
  });

  const updateStore = async (values: any) => {
    if (!activeCokitchen.id) return;
    setSubmitLoading(true);
    try {
      await apiService.put(`stores/cokitchen/${activeCokitchen.id}/stores/${id}`, {
        data: values
      });
      setRefreshStore(!refreshStore);
      navigate(MAIN_STORE_ROUTE_KEYS.GENERAL_STORE_DASHBOARD);
    } catch (e: any) {
      toast(<ToastMessage type='error' message={e.response.data.message} />);
    } finally {
      setSubmitLoading(false);
    }
  };
  return (
    <section className='bg-gray-1/40 rounded-md p-5 px-10'>
      <div className='flex justify-between gap-4'>
        <div className='flex-1'>
          <h2 className='text-base font-semibold'>Basic store details</h2>
          <p className='text-gray-8/80 text-sm max-w-md'>
            Lorem ipsum dolor sit amet consectetur. Morbi eu non pulvinar blandit amet ipsum. Enim
            orci fringilla diam maecenas.
          </p>
        </div>
        <div className='flex-1 flex justify-end'>
          <div className='bg-white max-w-md rounded-lg p-2 w-full'>
            {loading ? (
              <div className='flex justify-center mt-5'>
                <p>Loading </p>{' '}
              </div>
            ) : (
              <form onSubmit={form.handleSubmit(updateStore)}>
                <div className='m-4 space-y-10'>
                  <Controller
                    name={`name`}
                    rules={{
                      required: true
                    }}
                    control={form.control}
                    render={({ field }) => (
                      <Input
                        design='bordered'
                        label={'Name of store'}
                        placeholder={'Enter name of store'}
                        value={field.value}
                        onChange={(val) => field.onChange(val.target.value)}
                      />
                    )}
                  />
                  <Controller
                    name={`type`}
                    rules={{
                      required: true
                    }}
                    control={form.control}
                    render={({ field }) => (
                      <SelectDropdown
                        label='Type of store'
                        placeholder='Select store type'
                        value={field.value}
                        setValue={(val) => field.onChange(val?.id)}
                        options={[
                          { id: 'Bakery', name: 'Bakery' },
                          { id: 'Juice', name: 'Juice' },
                          { id: 'Cokitchen', name: 'Cokitchen' }
                        ]}
                      />
                    )}
                  />
                  <Controller
                    name={`location`}
                    rules={{
                      required: true
                    }}
                    control={form.control}
                    render={({ field }) => (
                      <SelectDropdown
                        label='Location'
                        placeholder='Select cokitchen location'
                        value={cokitchens.find((kitchen) => field.value === kitchen.id)?.name ?? ''}
                        setValue={(val) => field.onChange(val?.id)}
                        options={cokitchens?.map(({ name, id }) => {
                          return { name: name, id: id };
                        })}
                      />
                    )}
                  />
                  <Controller
                    name={`logo`}
                    rules={{
                      required: true
                    }}
                    control={form.control}
                    render={({ field }) => (
                      <div className='space-y-2'>
                        <label>Store logo</label>
                        <FileUploader
                          fileUrls={field.value ? [field.value] : []}
                          setFileUrls={(imageUrls: any) => {
                            field.onChange(imageUrls[0] ?? '');
                          }}
                          token={FILE_UPLOADER_TOKEN}
                          className='w-full border-none p-0 m-0'
                        />
                      </div>
                    )}
                  />

                  <Button
                    loading={submitLoading}
                    type='submit'
                    color='primary'
                    variant='block'
                    className='py-3 !mt-14'>
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BasicDetails;
